/* District Bangle — stack-builder.js
   Mirrors the design logic in Home.dc.html:
     subtotal = sum of picked prices (real variant prices when linked)
     discount = subtotal * pct  when count >= min      (preview only)
     total    = subtotal - discount
     discBar  = min(100, count/min * 100)
     shipBar  = min(100, total/shipThreshold * 100)
   The saving previews the merchant's automatic cart discount ("comes off
   automatically") — items are added at FULL price and Shopify applies it. */
import { BaseElement, define, money, afterAdd } from '@theme/global';

class StackBuilder extends BaseElement {
  init() {
    this.variant = this.dataset.variant || 'guided';
    this.min = Number(this.dataset.min) || 3;
    this.percent = Number(this.dataset.percent) || 20;
    this.shipThreshold = Number(this.dataset.ship) || 5000;
    this.unit = Number(this.dataset.unit) || 2500;

    /* Keyed by slot id, not by array index. A shopper can add any number of
       bangles and remove any of them, and a Map means removing the middle one
       does not renumber everything that follows. Insertion order is the display
       order, which is what Map already guarantees. */
    this.slots = new Map();
    this.slotSeq = 0;
    this.selection = [];   // grid: list of picks

    this.avatarRow = this.querySelector('[data-avatar-row]');
    this.emptyEl = this.querySelector('[data-empty]');
    this.cta = this.querySelector('[data-stack-checkout]');
    this.form = this.querySelector('[data-stack-form]');
    this.errorEl = this.querySelector('[data-stack-error]');
    this.emptyCta = this.cta ? this.cta.textContent.trim() : 'Pick a bangle to start';

    if (this.variant === 'guided') {
      this.slotList = this.querySelector('[data-slots]');
      this.tpl = this.querySelector('[data-slot-template]');
      this.wireSlot(this);
    } else {
      this.querySelectorAll('[data-toggle]').forEach((btn) =>
        this.listen(btn, 'click', () => this.toggle(btn)));
    }
    if (this.form) this.listen(this.form, 'submit', (e) => { e.preventDefault(); this.addAll(); });

    // "Add another bangle" reveals the next hidden slot; the button retires when
    // the last one is open, so the cap is the merchant's setting, not the design.
    this.moreBtn = this.querySelector('[data-add-slot]');
    if (this.moreBtn) this.moreBtn.addEventListener('click', () => this.addSlot());
    this.render();
  }

  data(btn) {
    return {
      key: btn.dataset.key,
      color: btn.dataset.color,
      glow: btn.dataset.glow,
      stone: btn.dataset.stone,
      stoneStyle: btn.dataset.stoneStyle || '',
      stoneImg: btn.dataset.stoneImg || '',
      img: btn.dataset.img || '',
      month: btn.dataset.month,
      variantId: btn.dataset.variantId ? Number(btn.dataset.variantId) : null,
      price: btn.dataset.price ? Number(btn.dataset.price) : this.unit,
      variants: this.variantsOf(btn),
    };
  }

  /* Which metal a finish name refers to, so the swatch beside it is the right
     colour. The same keyword mapping the variant picker uses; the colours
     themselves live in CSS, in one place. */
  metalOf(title) {
    const t = (title || '').toLowerCase();
    if (t.includes('rose')) return 'rose';
    if (t.includes('silver') || t.includes('rhodium') || t.includes('platinum') || t.includes('white')) return 'silver';
    if (t.includes('gold') || t.includes('brass')) return 'gold';
    if (t.includes('black') || t.includes('onyx')) return 'black';
    return 'other';
  }

  imageOf(p) {
    const v = (p.variants || []).find((x) => x.id === p.variantId);
    return (v && v.i) || p.img || '';
  }

  variantsOf(btn) {
    try { return JSON.parse(btn.dataset.variants || '[]'); } catch (_) { return []; }
  }

  /* The finish toggle lives where the chosen stone's name was. It is built from
     the picked product's own variants, so it says whatever that product offers
     rather than two hard-coded names, and it exists only while a stone is
     picked — clearing the stone puts "Choose" back. Without it every stack went
     to the cart as the first available finish, silently. */
  renderFinish(id) {
    const slotEl = this.querySelector(`[data-slot="${id}"]`);
    if (!slotEl) return;
    const box = slotEl.querySelector('[data-finish]');
    const chosenEl = slotEl.querySelector('[data-slot-chosen]');
    const item = this.slots.get(id);
    const list = item && item.variants && item.variants.length > 1 ? item.variants : [];
    if (!box) return;
    if (!list.length) {
      box.hidden = true; box.textContent = '';
      if (chosenEl) { chosenEl.hidden = false; chosenEl.textContent = item ? item.stone : (window.theme.strings.chooseStone || 'Choose'); }
      return;
    }
    if (chosenEl) chosenEl.hidden = true;
    box.textContent = '';
    /* Default to the product's first available finish — gold here — so the
       control always shows an answer rather than two unselected words. */
    if (!list.some((v) => v.id === item.variantId)) {
      const first = list.find((v) => v.a) || list[0];
      if (first) { item.variantId = first.id; item.price = first.p; item.finish = first.t; }
    }
    list.forEach((v) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.disabled = !v.a;
      b.dataset.metal = this.metalOf(v.t);
      // the visible label is hidden on narrow screens, so the name lives here too
      b.setAttribute('aria-label', v.t);
      b.setAttribute('aria-pressed', String(v.id === item.variantId));
      const dot = document.createElement('span');
      dot.className = 'dbstack__finish-dot';
      const label = document.createElement('span');
      label.textContent = v.t;
      b.append(dot, label);
      b.addEventListener('click', () => {
        const cur = this.slots.get(id);
        if (!cur || !v.a) return;
        cur.variantId = v.id;
        cur.price = v.p;
        cur.finish = v.t;
        this.renderFinish(id);
        this.render();
      });
      box.appendChild(b);
    });
    box.hidden = false;
  }

  /* 2a — one stone per slot; clicking the chosen chip clears it */
  pick(btn) {
    const id = btn.dataset.slotId;
    const item = this.data(btn);
    const current = this.slots.get(id);
    if (current && current.key === item.key) this.slots.delete(id);
    else this.slots.set(id, item);
    const chosen = this.slots.get(id);
    // reflect chip state within this slot only
    this.querySelectorAll(`[data-pick][data-slot-id="${id}"]`).forEach((b) => {
      const on = chosen && b.dataset.key === chosen.key;
      b.classList.toggle('is-on', !!on);
      b.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
    const slotEl = this.querySelector(`[data-slot="${id}"] [data-slot-chosen]`);
    if (slotEl) slotEl.textContent = chosen ? chosen.stone : (window.theme.strings.chooseStone || 'Choose');
    this.renderFinish(id);
    this.render();
  }

  /* 2b — toggle any stone */
  toggle(btn) {
    const item = this.data(btn);
    const idx = this.selection.findIndex((s) => s.key === item.key);
    if (idx >= 0) this.selection.splice(idx, 1); else this.selection.push(item);
    btn.setAttribute('aria-pressed', idx >= 0 ? 'false' : 'true');
    this.render();
  }

  /* Chips and the remove control inside `root`. Called for the section once and
     for each cloned row, so a row added ten minutes in behaves like the first. */
  wireSlot(root) {
    root.querySelectorAll('[data-pick]').forEach((btn) => {
      if (btn.dataset.wired) return;
      btn.dataset.wired = '1';
      btn.addEventListener('click', () => this.pick(btn));
    });
    const rm = root.querySelector ? root.querySelector('[data-remove-slot]') : null;
    if (rm && !rm.dataset.wired) {
      rm.dataset.wired = '1';
      rm.addEventListener('click', () => this.removeSlot(rm.closest('[data-slot]')));
    }
  }

  addSlot() {
    if (!this.tpl || !this.slotList) return;
    const id = `x${this.slotSeq += 1}`;
    const el = this.tpl.content.firstElementChild.cloneNode(true);
    el.dataset.slot = id;
    el.querySelectorAll('[data-pick]').forEach((b) => { b.dataset.slotId = id; });
    this.slotList.appendChild(el);
    this.wireSlot(el);
    this.renumber();
    el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }

  removeSlot(el) {
    if (!el) return;
    this.slots.delete(el.dataset.slot);
    el.remove();
    this.renumber();
    this.render();
  }

  renumber() {
    [...this.querySelectorAll('[data-slot]')].forEach((el, i) => {
      const n = el.querySelector('.dbstack__slot-num');
      if (n) n.textContent = String(i + 1).padStart(2, '0');
    });
  }

  picks() {
    return this.variant === 'guided' ? [...this.slots.values()].filter(Boolean) : this.selection;
  }

  render() {
    const picks = this.picks();
    const n = picks.length;
    const subtotal = picks.reduce((sum, p) => sum + p.price, 0);
    const qualifies = n >= this.min && this.percent > 0;
    const discount = qualifies ? Math.round(subtotal * (this.percent / 100)) : 0;
    const total = subtotal - discount;

    // avatars
    if (this.avatarRow) {
      this.avatarRow.hidden = n === 0;
      this.avatarRow.textContent = '';
      picks.forEach((p) => {
        const src = this.imageOf(p);
        const cell = document.createElement('span');
        cell.className = 'dbstack__pic';
        if (src) {
          const im = document.createElement('img');
          im.src = src;
          im.loading = 'lazy';
          im.width = 160; im.height = 160;
          // Built as nodes, not interpolated HTML: a stone name is merchant copy
          // and has no business being parsed as markup.
          im.alt = [p.month, p.stone].filter(Boolean).join(' ');
          cell.appendChild(im);
        } else {
          // A product with no photo still has its stone — better than a hole.
          // Nested rather than merged onto the cell: `.db-stone` carries its own
          // circle radius and would win over the cell's.
          const dot = document.createElement('span');
          dot.className = `dbstack__pic-stone db-stone ${p.stoneImg || ''}`.trim();
          dot.setAttribute('style', p.stoneStyle || `--stone:${p.color};--glow:${p.glow}`);
          cell.appendChild(dot);
        }
        this.avatarRow.appendChild(cell);
      });
    }
    if (this.emptyEl) this.emptyEl.hidden = n > 0;

    // counts
    this.querySelectorAll('[data-stack-count]').forEach((el) => { el.textContent = n; });

    // discount meter
    const toDisc = Math.max(0, this.min - n);
    const discMsg = this.querySelector('[data-disc-msg]');
    const discPct = this.querySelector('[data-disc-pct]');
    const discBar = this.querySelector('[data-disc-bar]');
    const strings = (window.theme && window.theme.strings) || {};
    if (discMsg) discMsg.textContent = qualifies
      ? (strings.stackApplied || '✦ {{ percent }}% discount applied').replace('{{ percent }}', this.percent)
      : (strings.stackHint || 'Add {{ count }} more to save {{ percent }}%')
          .replace('{{ count }}', toDisc).replace('{{ percent }}', this.percent);
    if (discPct) discPct.textContent = qualifies ? `${this.percent}% off` : `${this.percent}% at ${this.min}`;
    if (discBar) discBar.style.width = `${Math.min(100, (n / this.min) * 100)}%`;

    // shipping meter
    const shipRem = Math.max(0, this.shipThreshold - total);
    const shipMsg = this.querySelector('[data-ship-msg]');
    const shipBar = this.querySelector('[data-ship-bar]');
    if (shipMsg) shipMsg.textContent = total >= this.shipThreshold
      ? '✓ Free U.S. shipping unlocked'
      : `${money(shipRem)} from free shipping`;
    if (shipBar) shipBar.style.width = `${Math.min(100, (total / this.shipThreshold) * 100)}%`;

    // totals
    const set = (sel, v) => { const el = this.querySelector(sel); if (el) el.textContent = v; };
    set('[data-subtotal]', money(subtotal));
    set('[data-discount]', discount > 0 ? `– ${money(discount)}` : money(0));
    set('[data-total]', money(total));

    // CTA
    if (this.cta) {
      this.cta.disabled = n === 0;
      this.cta.classList.remove('is-added');
      this.cta.textContent = n ? `Add ${n} to cart · ${money(total)}` : this.emptyCta;
    }
  }

  async addAll() {
    const picks = this.picks();
    if (!picks.length) {
      if (this.errorEl) { this.errorEl.textContent = 'Pick a bangle to start.'; this.errorEl.hidden = false; }
      return;
    }
    /* What is shown is what is added. This used to drop every pick with no linked
       product and add the remainder — the button said "Add 3 to cart" and two
       arrived, quietly, reported as success. Now nothing is added unless all of
       it can be, and the ones at fault are named. */
    const unbuyable = picks.filter((p) => !p.variantId);
    if (unbuyable.length) {
      if (this.errorEl) {
        const names = unbuyable.map((p) => p.stone || p.month).filter(Boolean).join(', ');
        this.errorEl.textContent = `No product is linked to ${names}, so your stack was not added. Remove it, or pick another stone.`;
        this.errorEl.hidden = false;
      }
      return;
    }
    if (this.cta) this.cta.setAttribute('aria-busy', 'true');
    try {
      const res = await fetch(window.theme.routes.cart_add, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
        body: JSON.stringify({ items: picks.map((p) => ({ id: p.variantId, quantity: 1 })) }),
      });
      const data = await res.json();
      if (data.status) throw new Error(data.description || data.message);
      afterAdd();   // honour the merchant's "After adding to cart" choice, like every other add path
      if (this.cta) { this.cta.classList.add('is-added'); this.cta.textContent = '✓ Added to cart'; }
      setTimeout(() => this.render(), 1800);
      if (this.errorEl) this.errorEl.hidden = true;
    } catch (err) {
      if (this.errorEl) { this.errorEl.textContent = err.message || 'Could not add your stack.'; this.errorEl.hidden = false; }
    } finally {
      if (this.cta) this.cta.removeAttribute('aria-busy');
    }
  }
}

define('stack-builder', StackBuilder);
